import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, MapPin, Briefcase, Languages, Ruler, Flag, Ban, Star, Home, Plane, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PhotoImg, VerifiedBadge, OfferChip, GoldButton, GhostButton } from "../components/common";
import { formatHeight } from "../components/HeightInput";
import { MakeOfferModal } from "../components/OfferModals";
import { useCompatibility, CompatibilityCard } from "../components/AstrologyCompatibility";
import CareerFinancialDisplay from "../components/CareerFinancialDisplay";
import { GiftShowcase } from "../components/GiftModal";

function Info({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-white/6">
      <Icon size={16} className="text-[#D4AF37]" />
      <span className="text-xs uppercase tracking-wide text-[#9E978E] w-28">{label}</span>
      <span className="text-sm text-[#FBF8F3]">{value}</span>
    </div>
  );
}

export default function ProfileDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], average: null, count: 0 });
  const [active, setActive] = useState(0);
  const [showOffer, setShowOffer] = useState(false);
  const [fav, setFav] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportCat, setReportCat] = useState("profile");
  const [reportReason, setReportReason] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const { data: compat } = useCompatibility(userId);

  const load = async () => {
    try {
      const { data } = await api.get(`/profile/${userId}`);
      setP(data);
      const rv = await api.get(`/users/${userId}/reviews`);
      setReviews(rv.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Profile unavailable"); navigate("/discover"); }
  };
  useEffect(() => { load(); }, [userId]);

  const toggleFav = async () => {
    try { fav ? await api.delete(`/favorites/${userId}`) : await api.post(`/favorites/${userId}`); setFav(!fav); }
    catch (e) { toast.error("Could not update"); }
  };
  const block = async () => { try { await api.post(`/blocks/${userId}`); toast.success("Member blocked"); navigate("/discover"); } catch (e) { toast.error("Failed"); } };
  const submitReport = async () => {
    if (!reportReason.trim()) return toast.error("Please describe the issue");
    setReportBusy(true);
    try {
      await api.post("/reports", { target_user_id: userId, reason: reportReason.trim(), category: reportCat });
      toast.success("Report submitted — our team will review it");
      setShowReport(false); setReportReason(""); setReportCat("profile");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setReportBusy(false); }
  };

  if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-[#9E978E]">Loading…</div>;
  const photos = p.photos || [];
  const activePhoto = photos[active]?.photo_id || p.main_photo_id;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#9E978E] hover:text-[#FBF8F3] mb-5"><ArrowLeft size={16} /> Back to discovery</button>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 db-fade-up">
            <PhotoImg photoId={activePhoto} alt={p.display_name} fallbackInitial={p.display_name?.[0]} className="w-full h-full object-cover" />
            <div className="absolute top-3 left-3"><VerifiedBadge selfie={p.selfie_verified} id={p.id_verified} size="md" /></div>
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
              {photos.map((ph, i) => (
                <button key={ph.photo_id} onClick={() => setActive(i)} className={`h-16 w-16 rounded-lg overflow-hidden border-2 shrink-0 ${i === active ? "border-[#D4AF37]" : "border-transparent opacity-70"}`}>
                  <PhotoImg photoId={ph.photo_id} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {(p.private_photo_count > 0) && (
            <div data-testid="private-photos-section" className="mt-5 rounded-2xl border border-[#D4AF37]/20 bg-[#141113]/60 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={14} className="text-[#D4AF37]" />
                <h3 className="text-xs uppercase tracking-[0.15em] text-[#C9A96E]">Private Photos ({p.private_photo_count})</h3>
                {p.private_unlocked && <span data-testid="private-unlocked-badge" className="text-[10px] px-2 py-0.5 rounded-full bg-[#1f6b43]/30 text-[#7FD6A3]">Unlocked</span>}
              </div>
              {p.private_unlocked ? (
                <p className="text-xs text-[#9E978E]">These private photos are unlocked for you and appear in the gallery above.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: Math.min(p.private_photo_count, 8) }).map((_, i) => (
                      <div key={i} data-testid="private-photo-locked" className="relative h-20 w-16 rounded-lg border border-white/10 bg-[#0C0A0A]/70 flex flex-col items-center justify-center text-[#9E978E] gap-1">
                        <Lock size={16} />
                        <span className="text-[8px] uppercase tracking-wide">Private</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#9E978E] mt-2">Private photos unlock only when {p.display_name || "this member"} accepts your date offer.</p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="db-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-serif text-4xl text-[#FBF8F3] leading-tight">{p.display_name}{p.age ? `, ${p.age}` : ""}</h1>
              {p.zodiac && <p className="text-[#C9A96E] text-sm mt-1" data-testid="profile-zodiac">{p.age ? `${p.age} · ` : ""}{p.zodiac.symbol} {p.zodiac.name}</p>}
              {p.location && <p className="text-[#D8D2C9] flex items-center gap-1.5 mt-1"><MapPin size={15} />{p.location}{p.approx_distance ? ` · ${p.approx_distance}` : ""}</p>}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-[#1C171A] border border-white/10 text-[#9E978E] px-2.5 py-1 text-xs"><Home size={12} /> Lives in {p.location || "—"}</span>
                {p.visiting && <span className="inline-flex items-center gap-1 rounded-full bg-[#541224]/60 border border-[#D4AF37]/30 text-[#D4AF37] px-2.5 py-1 text-xs"><Plane size={12} /> {p.visiting.active ? "Currently visiting" : "Visiting"} {p.visiting_location} · {(p.visiting.arrival_date||"").slice(5)}–{(p.visiting.departure_date||"").slice(5)}</span>}
              </div>
              {p.headline && <p className="text-[#C9A96E] italic font-serif text-lg mt-2">"{p.headline}"</p>}
            </div>
            <button onClick={toggleFav} className={`h-11 w-11 rounded-full flex items-center justify-center border transition-colors ${fav ? "bg-[#751A32] border-[#751A32] text-white" : "border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#541224]/40"}`}><Heart size={19} fill={fav ? "currentColor" : "none"} /></button>
          </div>

          <div className="flex items-center gap-3 mt-5">
            <OfferChip amount={p.preferred_offer} />
            {reviews.average && <div className="flex items-center gap-1.5 text-sm text-[#D4AF37]"><Star size={16} fill="currentColor" /> {reviews.average} <span className="text-[#9E978E]">({reviews.count})</span></div>}
          </div>

          {p.is_demo ? (
            <div data-testid="detail-demo-notice" className="mt-5 rounded-2xl border border-[#D4AF37]/40 bg-[#541224]/30 p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37]" data-testid="detail-demo-badge">{p.account_class === "test" ? "Test Account" : "Demo Profile"}</div>
              <p className="text-sm text-[#D8D2C9] mt-1">This profile shows how DateBid works and isn't a real member. Paid offers, messaging unlocks and gifts are turned off.</p>
            </div>
          ) : (
            <GoldButton data-testid="detail-place-offer-btn" onClick={() => setShowOffer(true)} className="w-full mt-5 py-3.5 text-base">Place a Date Offer</GoldButton>
          )}

          <CompatibilityCard data={compat} />

          {p.about_me && (
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">About</div>
              <p className="text-[#D8D2C9] leading-relaxed">{p.about_me}</p>
            </div>
          )}

          <div className="mt-6">
            <Info icon={Briefcase} label="Occupation" value={p.occupation} />
            <Info icon={Ruler} label="Height" value={formatHeight(p.height_cm)} />
            <Info icon={Languages} label="Languages" value={(p.languages || []).join(", ") || null} />
          </div>

          <GiftShowcase userId={p.user_id} />

          {p.career_financial && (
            <div className="mt-6">
              <CareerFinancialDisplay cf={p.career_financial} />
            </div>
          )}

          {(p.primary_intention || (p.dating_intentions || []).length > 0) && (
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">Looking For</div>
              <div className="flex flex-wrap gap-2">
                {p.primary_intention && <span className="rounded-full bg-[#751A32] border border-[#D4AF37]/50 px-3 py-1 text-sm text-[#FBF8F3]">{p.primary_intention}</span>}
                {(p.dating_intentions || []).filter((d) => d !== p.primary_intention).map((i) => <span key={i} className="rounded-full bg-[#541224]/40 border border-[#D4AF37]/20 px-3 py-1 text-sm text-[#FBF8F3]">{i}</span>)}
              </div>
            </div>
          )}

          {(p.interests || []).length > 0 && (
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">Interests</div>
              <div className="flex flex-wrap gap-2">{p.interests.map((i) => <span key={i} className="rounded-full bg-[#1C171A] border border-white/8 px-3 py-1 text-sm text-[#C9A96E]">{i}</span>)}</div>
            </div>
          )}

          {(p.hobbies || []).length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">Hobbies</div>
              <div className="flex flex-wrap gap-2">{p.hobbies.map((i) => <span key={i} className="rounded-full bg-[#1C171A] border border-white/8 px-3 py-1 text-sm text-[#C9A96E]">{i}</span>)}</div>
            </div>
          )}

          {(p.date_preferences || []).length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">Ideal Date</div>
              <div className="flex flex-wrap gap-2">{p.date_preferences.map((i) => <span key={i} className="rounded-full bg-[#541224]/40 border border-[#D4AF37]/20 px-3 py-1 text-sm text-[#FBF8F3]">{i}</span>)}</div>
            </div>
          )}

          <div className="flex gap-3 mt-7 pt-5 border-t border-white/8">
            <GhostButton data-testid="report-btn" onClick={() => setShowReport(true)} className="flex-1 flex items-center justify-center gap-2 text-xs"><Flag size={14} /> Report</GhostButton>
            <GhostButton data-testid="block-btn" onClick={block} className="flex-1 flex items-center justify-center gap-2 text-xs"><Ban size={14} /> Block</GhostButton>
          </div>
        </div>
      </div>

      {reviews.reviews.length > 0 && (
        <div className="mt-10">
          <div className="db-gold-rule mb-6" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-4">Date Reviews</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {reviews.reviews.map((r, i) => (
              <div key={i} className="rounded-2xl border border-white/8 bg-[#141113]/60 p-5">
                <div className="flex items-center gap-1 text-[#D4AF37] mb-2">{Array.from({ length: r.overall }).map((_, j) => <Star key={j} size={14} fill="currentColor" />)}</div>
                {r.feedback && <p className="text-sm text-[#D8D2C9] italic">"{r.feedback}"</p>}
                <div className="flex gap-3 mt-3 text-[11px] text-[#9E978E]">
                  {r.showed_up && <span>✓ Showed up</span>}{r.respectful && <span>✓ Respectful</span>}{r.would_meet_again && <span>✓ Would meet again</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showOffer && <MakeOfferModal recipient={{ user_id: userId, display_name: p.display_name, preferred_offer: p.preferred_offer }} compatibility={compat} onClose={() => setShowOffer(false)} onDone={() => toast.success("View it under Your Offers")} />}

      {showReport && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70" data-testid="report-modal">
          <div className="w-full max-w-md rounded-2xl border border-[#D4AF37]/25 bg-[#141113] p-6">
            <h3 className="font-serif text-lg text-[#FBF8F3] mb-1 flex items-center gap-2"><Flag size={16} className="text-[#E58CA0]" /> Report {p.display_name}</h3>
            <p className="text-[11px] text-[#9E978E] mb-4">Help us keep DateBid safe. Our team reviews every report.</p>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">What's the issue about?</label>
            <select data-testid="report-category" value={reportCat} onChange={(e) => setReportCat(e.target.value)}
              className="w-full mt-1 mb-3 rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-3 py-2.5 text-sm text-[#FBF8F3]">
              <option value="profile">Profile / member behavior</option>
              <option value="photo">A photo</option>
              <option value="message">Messages / conversation</option>
              <option value="other">Something else</option>
            </select>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Details</label>
            <textarea data-testid="report-reason" rows={4} value={reportReason} maxLength={500} onChange={(e) => setReportReason(e.target.value)}
              className="w-full mt-1 rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-3 py-2.5 text-sm text-[#FBF8F3] resize-none" placeholder="Describe what happened…" />
            <div className="flex items-center gap-2 mt-4">
              <GoldButton data-testid="report-submit" onClick={submitReport} disabled={reportBusy} className="flex-1">{reportBusy ? "Submitting…" : "Submit Report"}</GoldButton>
              <button onClick={() => setShowReport(false)} className="px-4 py-2 text-sm text-[#9E978E] hover:text-[#FBF8F3]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
